import { HostnameService } from 'src/app/hostname-router-service';
import { ReportVizChartColors } from './constants';
import * as Highcharts from 'highcharts';

export function getChartEvents(position: 'right' | 'left' | 'center' = 'right') {
  return HostnameService.isWorkforceHostname()
    ? getChartEventsWorkforce(position)
    : getChartEventsGotlivedata(position);
}

function getChartEventsWorkforce(position: 'right' | 'left' | 'center' = 'right') {
  const logoUrl = 'assets/img/workforce_logo_white.svg';
  // 870 x 132 workforce logo
  // 202 x 40 ldt logo
  return {
    load: function () {
      const chart = this as any;
      const logoX =
        position === 'right'
          ? chart.chartWidth - 131
          : position === 'center'
            ? chart.chartWidth / 2 - 60
            : 10;

      chart.customLogo = chart.renderer
        .image(logoUrl, logoX, chart.chartHeight - 34, 121, 18)
        .add();

      // Add the class to the <img> element
      if (chart.customLogo && chart.customLogo.element) {
        chart.customLogo.element.setAttribute('class', 'ldt-logo');
      }
    },
    redraw: function () {
      const chart = this as any;
      const logo = chart.customLogo;
      let logoWidth = 121;
      let logoHeight = 18;
      if (chart.chartWidth < 480) {
        logoWidth = 90; // Smaller width for smaller containers
        logoHeight = logoWidth / 6.59; // Maintain aspect ratio
      }
      const logoX =
        position === 'right'
          ? chart.chartWidth - (logoWidth + 10)
          : position === 'center'
            ? chart.chartWidth / 2 - logoWidth / 2
            : 10;

      if (logo) {
        logo.attr({
          x: logoX,
          y: chart.chartHeight - (logoHeight + 10),
          width: logoWidth,
          height: logoHeight,
        });
      }
    },
  };
}

function getChartEventsGotlivedata(position: 'right' | 'left' | 'center' = 'right') {
  const logoUrl = 'assets/img/logo-white.svg';
  return {
    load: function () {
      const chart = this as any;
      const logoX =
        position === 'right'
          ? chart.chartWidth - 131
          : position === 'center'
            ? chart.chartWidth / 2 - 60
            : 10;

      chart.customLogo = chart.renderer
        .image(logoUrl, logoX, chart.chartHeight - 34, 121, 24)
        .add();

      // Add the class to the <img> element
      if (chart.customLogo && chart.customLogo.element) {
        chart.customLogo.element.setAttribute('class', 'ldt-logo');
      }
    },
    redraw: function () {
      const chart = this as any;
      const logo = chart.customLogo;
      let logoWidth = 121;
      let logoHeight = 24;
      if (chart.chartWidth < 480) {
        logoWidth = 90; // Smaller width for smaller containers
        logoHeight = logoWidth / 5.04; // Maintain aspect ratio
      }
      const logoX =
        position === 'right'
          ? chart.chartWidth - (logoWidth + 10)
          : position === 'center'
            ? chart.chartWidth / 2 - logoWidth / 2
            : 10;

      if (logo) {
        logo.attr({
          x: logoX,
          y: chart.chartHeight - (logoHeight + 10),
          width: logoWidth,
          height: logoHeight,
        });
      }
    },
  };
}

function tooltipValueLabel(thisParam: any, useXLabel = false): string {
  const series = thisParam.series;
  const pointValue = thisParam.y || 0;
  const yAxisIndicator =
    !!(series?.yAxis as any)?.axisTitle?.textStr &&
    /(%|pct)/i.test((series?.yAxis as any).axisTitle.textStr);
  const isPercentage = yAxisIndicator || series?.userOptions?.custom?.isPct;

  const trimmedPoint =
    Math.abs(pointValue) < 10
      ? pointValue.toFixed(1)
      : pointValue.toLocaleString(undefined, { maximumFractionDigits: 0 });

  const formattedValue = isPercentage ? trimmedPoint + '%' : trimmedPoint;

  return `${useXLabel ? thisParam.x : series.name}: <b>${formattedValue}</b><br />`;
}

function tooltipPointFormatter(point: Highcharts.Point): string {
  return tooltipValueLabel(point);
}

export const formatLargeNumber = (value: number | string, decs: number = 1): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value >= 1000000) {
    if (value % 1000000 === 0) decs = 0;
    return (value / 1000000).toFixed(decs) + 'M';
  } else if (value >= 1000) {
    if (value % 1000 === 0) decs = 0;
    return (value / 1000).toFixed(decs) + 'K';
  }
  return value.toString();
};

Highcharts.SVGRenderer.prototype.symbols.fullscreen = function (
  x: number,
  y: number,
  w: number,
  h: number
) {
  const cornerSize = Math.min(w, h) * 0.3; // size of corner brackets
  const gap = Math.min(w, h) * 0.15; // gap between corners

  const path = [
    // Top-left corner
    'M',
    x,
    y + cornerSize,
    'L',
    x,
    y,
    'L',
    x + cornerSize,
    y,

    // Top-right corner
    'M',
    x + w - cornerSize,
    y,
    'L',
    x + w,
    y,
    'L',
    x + w,
    y + cornerSize,

    // Bottom-right corner
    'M',
    x + w,
    y + h - cornerSize,
    'L',
    x + w,
    y + h,
    'L',
    x + w - cornerSize,
    y + h,

    // Bottom-left corner
    'M',
    x + cornerSize,
    y + h,
    'L',
    x,
    y + h,
    'L',
    x,
    y + h - cornerSize,
  ];

  return path;
};

Highcharts.SVGRenderer.prototype.symbols.settings = function (
  x: number,
  y: number,
  w: number,
  h: number
) {
  const cx = x + w * 0.5;
  const cy = y + h * 0.5;
  const r = Math.min(w, h) * 0.45; // base radius
  const teeth = 6;
  const toothHeight = r * 0.4;
  const toothWidth = 10; // degrees

  const path = [];

  for (let i = 0; i < teeth; i++) {
    const baseAngle = (i * 360) / teeth - 90; // start from top
    const angleRad = (baseAngle * Math.PI) / 180;
    const toothStartRad = ((baseAngle - toothWidth / 2) * Math.PI) / 180;
    const toothEndRad = ((baseAngle + toothWidth / 2) * Math.PI) / 180;

    // Tooth outer corners
    const x1 = cx + Math.cos(toothStartRad) * (r + toothHeight);
    const y1 = cy + Math.sin(toothStartRad) * (r + toothHeight);
    const x2 = cx + Math.cos(toothEndRad) * (r + toothHeight);
    const y2 = cy + Math.sin(toothEndRad) * (r + toothHeight);

    // Connect to base circle
    const nextAngle = ((((i + 1) * 360) / teeth - 90 - toothWidth / 2) * Math.PI) / 180;
    const x3 = cx + Math.cos(toothEndRad) * r;
    const y3 = cy + Math.sin(toothEndRad) * r;
    const x4 = cx + Math.cos(nextAngle) * r;
    const y4 = cy + Math.sin(nextAngle) * r;

    if (i === 0) {
      path.push('M', x1, y1);
    } else {
      path.push('L', x1, y1);
    }

    path.push('L', x2, y2);
    path.push('L', x3, y3);
    path.push('A', r, r, 0, 0, 1, x4, y4);
  }

  path.push('Z');

  // Inner circle (hole)
  const innerR = r * 0.6;
  path.push('M', cx + innerR, cy);
  path.push('A', innerR, innerR, 0, 0, 0, cx - innerR, cy);
  path.push('A', innerR, innerR, 0, 0, 0, cx + innerR, cy);
  path.push('Z');

  return path;
};

Highcharts.SVGRenderer.prototype.symbols.download = function (
  x: number,
  y: number,
  w: number,
  h: number
) {
  const path = [
    // Arrow stem
    'M',
    x + w * 0.5,
    y,
    'L',
    x + w * 0.5,
    y + h * 0.7,
    // Arrow head
    'M',
    x + w * 0.3,
    y + h * 0.5,
    'L',
    x + w * 0.5,
    y + h * 0.7,
    'L',
    x + w * 0.7,
    y + h * 0.5,
    // Box
    'M',
    x,
    y + h * 0.9,
    'L',
    x,
    y + h,
    'L',
    x + w,
    y + h,
    'L',
    x + w,
    y + h * 0.9,
  ];
  return path;
};

export const reportBaseOptions: Highcharts.Options = {
  colors: ReportVizChartColors,
  caption: {
    useHTML: true,
    style: {
      fontSize: '.6em',
    },
  },
  lang: {
    loading: 'Loading chart data...',
  },
  credits: {
    enabled: false,
  },
  accessibility: {
    enabled: false,
  },
  title: {
    text: 'Headcount',
    align: 'center',
    minScale: 0.85,
    style: {
      fontWeight: '600',
    },
  },
  chart: {
    spacingTop: 15, // a bit of top padding
    events: getChartEvents(),
  },
  plotOptions: {
    bar: {
      cursor: undefined,
      borderWidth: 0,
      borderColor: 'transparent',
      dataLabels: {
        enabled: true,
        style: {
          backgroundColor: 'white',
          fontWeight: 'bold',
          fontSize: '13px',
          textOutline: '3px #ffffff',
        },
        color: '#000000',
      },
    },
    series: {
      label: {
        connectorAllowed: false,
      },
      states: {
        hover: {
          enabled: false, // Disable default hover // NOT IN PREV
        },
      },
      marker: {
        enabled: true,
        radius: 2,
        symbol: 'circle',
      },
      dataLabels: {
        enabled: false,
      },
      linecap: 'round', // Make the lines rounded instead of angular
    },
    column: {
      stacking: 'normal',
      borderWidth: 0,
      borderColor: 'transparent',
    },
    sankey: {
      dataLabels: {
        enabled: true,
        style: {
          fontWeight: 'bold',
          fontSize: '13px',
          textOutline: '3px #ffffff',
          width: 200,
        },
        color: '#000000',
      },
      states: {
        hover: {
          enabled: true,
        },
      },
      nodeWidth: 200,
      nodePadding: 8,
    },
    treemap: {
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '13px',
          fontWeight: 'bold',
          color: '#000000',
          textOverflow: 'ellipsis',
          textOutline: '3px #ffffff',
        },
      },
    },
  },
  tooltip: {
    useHTML: true,
    // formatter: groupIconTooltipFormatter,
    pointFormatter: function (this: Highcharts.Point): string {
      return tooltipPointFormatter(this);
    },
  },
  exporting: {
    buttons: {
      contextButton: {
        buttonSpacing: 80,
        symbol: 'download',
      },
      zoomButton: {
        symbol: 'fullscreen',
        onclick: function () {
          const chart = this as Highcharts.Chart;
          chart.fullscreen.toggle();
        },
        x: -30,
        y: -5,
      },
    },
    sourceWidth: 800 / 1.2,
    sourceHeight: 600 / 1.2,
    scale: 1.2,
    chartOptions: {
      legend: {
        itemMarginBottom: 0,
        itemWidth: 120,
        itemStyle: {
          lineHeight: 1.2,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
        navigation: {
          enabled: false,
        },
      },
      caption: {
        style: {
          fontSize: '0.8em',
          fontFamily: 'monospace',
        },
        margin: 10,
        y: 15,
      },
      plotOptions: {
        sankey: {
          dataLabels: {
            padding: 0,
          },
        },
      },
    },
  },
  legend: {
    maxHeight: undefined,
  },
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 480,
        },
        chartOptions: {
          legend: {
            maxHeight: 65,
          },
        },
      },
    ],
  },
};

export const reportChartLineOptions = Highcharts.merge({}, reportBaseOptions, {
  chart: {
    type: 'line',
    spacingBottom: 50,
  },
  xAxis: {
    type: 'datetime',
    dateTimeLabelFormats: {
      day: '%e %b %Y', // format for the dates on the xAxis
    },
  },
  yAxis: [
    {
      allowDecimals: false,
      title: {
        text: 'Average Tenure (months)',
      },
      stackLabels: {
        enabled: false,
      },
    },
  ],
  legend: {
    useHTML: true,
    layout: 'horizontal',
    align: 'left',
    verticalAlign: 'top',
    // labelFormatter: function () { // TODO WAS IN PREV
    //   return labelFormatter(this as Highcharts.Series);
    // },
  },
});

export const reportChartBarOptions = Highcharts.merge({}, reportBaseOptions, {
  chart: {
    type: 'bar',
    events: getChartEvents('left'),
  },
  caption: {
    align: 'right',
  },
  tooltip: {
    enabled: true,
    useHTML: true,
    formatter: function (this: Highcharts.Point) {
      return tooltipValueLabel(this, true);
    },
  },
});

export const reportSankeyOptions = Highcharts.merge({}, reportBaseOptions, {
  chart: {
    events: getChartEvents('center'),
  },
  tooltip: {
    pointFormatter: null,
  },
  legend: { enabled: false },
  yAxis: { labels: { enabled: false } },
  exporting: {
    scale: 1.1,
    sourceWidth: 800 / 1.1,
    sourceHeight: 600 / 1.1,
  },
});

export const reportTreemapOptions = Highcharts.merge({}, reportBaseOptions, {
  chart: {
    events: getChartEvents('center'),
    type: 'treemap',
    marginBottom: 40,
  },
  tooltip: {
    pointFormatter: null,
  },
});

export const reportMapOptions = Highcharts.merge({}, reportBaseOptions, {
  chart: {
    borderWidth: 0,
  },
  legend: {
    layout: 'vertical',
    align: 'right',
    borderWidth: 0,
    verticalAlign: 'middle',
  },
  mapNavigation: {
    enabled: true,
    enableMouseWheelZoom: false,
  },
  tooltip: {
    enabled: true,
    formatter: null,
    pointFormatter: null,
  },
  colorAxis: {
    min: 0,
    maxColor: ReportVizChartColors[0],
  },
  exporting: {
    chartOptions: {
      legend: {
        x: 60, // Slight offset from the right edge
        navigation: {
          enabled: false,
        },
      },
    },
  },
});

export const columnChartXAaxis: Highcharts.XAxisOptions = {
  type: 'category',
  categories: undefined,
  labels: {
    useHTML: true,
    formatter: function () {
      const hasGroup = this.value.toString().endsWith('(groupicon)');
      const val = this.value.toString().replace('(groupicon)', '');
      return (
        '' +
        val +
        (hasGroup
          ? ' <img src="../../assets/img/office-building.png" alt="office building conglomerate" class="tw-w-4 tw-align-middle tw-mb-1">'
          : '')
      );
    },
  },
};

export const timeseriesChartXaxis: Highcharts.XAxisOptions = {
  type: 'datetime',
  categories: undefined,
  dateTimeLabelFormats: {
    day: '%e %b %Y', // format for the dates on the xAxis
  },
  labels: {
    formatter: undefined,
  },
};

const arrDepOriginalOptions: Highcharts.Options = {
  caption: {
    useHTML: true,
    style: {
      fontSize: '.6em',
    },
    text: 'Source: Live Data Technologies',
  },
  chart: {
    spacingTop: 15, // a bit of top padding
    events: getChartEvents(),
  },
  credits: {
    enabled: false,
  },
  title: {
    text: 'Headcount',
    align: 'center',
    style: {
      fontWeight: '600',
    },
  },
  yAxis: [
    {
      allowDecimals: false,
      title: {
        text: 'Number of Employees',
      },
      stackLabels: {
        enabled: false,
      },
      labels: {
        formatter: function () {
          return formatLargeNumber(this.value);
        },
      },
    },
  ],
  plotOptions: {
    series: {
      label: {
        connectorAllowed: false,
      },
      cursor: 'pointer',
      marker: {
        enabled: false, // Disable data points
      },
      dataLabels: {
        enabled: false,
      },
      linecap: 'round', // Make the lines rounded instead of angular
    },
    column: {
      stacking: 'normal',
      borderWidth: 0,
      borderColor: 'transparent',
    },
  },
  legend: {
    layout: 'horizontal',
    align: 'left',
    verticalAlign: 'top',
    useHTML: true,
  },
  tooltip: {
    useHTML: true,
  },
};

export const arrDepChartNoSeries: Highcharts.Options = Highcharts.merge(
  {},
  reportBaseOptions,
  reportChartLineOptions,
  arrDepOriginalOptions
);

export const pctValuesChartOptions: Highcharts.Options = {
  plotOptions: {
    series: {
      dataLabels: {
        enabled: true,
        formatter: function () {
          const value = this.y || 0;
          return `${Math.abs(value) < 10 ? Highcharts.numberFormat(value, 1) : Highcharts.numberFormat(value, 0)}%`;
        },
      },
    },
  },
};

export const numberValuesChartOptions: Highcharts.Options = {
  plotOptions: {
    series: {
      dataLabels: {
        enabled: true,
        formatter: function () {
          const pointValue = this.y || 0;
          const trimmedPoint =
            Math.abs(pointValue) < 10
              ? pointValue.toFixed(1)
              : pointValue.toLocaleString(undefined, { maximumFractionDigits: 0 });

          return trimmedPoint;
        },
      },
    },
  },
};

export const spiderChartConfig: Highcharts.Options = Highcharts.merge({}, reportBaseOptions, {
  chart: {
    polar: true,
    type: 'line',
    parallelCoordinates: true,
    parallelAxes: {
      labels: {
        style: {
          color: '#999999',
        },
      },
      lineWidth: 2,
      showFirstLabel: false,
      showLastLabel: false,
    },
  },
  xAxis: {
    gridLineWidth: 0,
    tickmarkPlacement: 'on',
    lineWidth: 0,
  },
  yAxis: {
    gridLineInterpolation: 'polygon',
    lineWidth: 0,
    min: 0,
    allowDecimals: false,
    title: {
      text: undefined,
    },
    stackLabels: {
      enabled: false,
    },
  },
  plotOptions: {
    series: {
      label: {
        connectorAllowed: false,
      },
      cursor: 'pointer',
      marker: {
        enabled: false, // Disable data points
      },
      dataLabels: {
        enabled: false,
      },
      linecap: 'round', // Make the lines rounded instead of angular
    },
    column: {
      stacking: 'normal',
    },
  },
  pane: {
    size: '80%',
    background: [
      {
        backgroundColor: 'var(--dark-white-dynamic-bg)',
        borderWidth: 0,
      },
    ],
  },
});
